/**
 * "Do these rows need to say which company they belong to?" — one answer, for
 * every surface that offers company-scoped rows the reader did not scope.
 *
 * The fourth arm of the "which company?" question. `CompanyScopeSwitcher` filters
 * a collection, `PageCompanyIndicator` states a record's own company,
 * `useRowCompanyVisible` gates the per-row indicator on a LIST — and this gates it
 * on a set of OPTIONS: a picker's dropdown, a `<Select>`'s item list, a dialog's
 * radio group.
 *
 * It is deliberately NOT `useRowCompanyVisible`. That gate's three clauses read the
 * ROUTE (`isCompanyScopedPath(pathname)`) and the app-bar scope, neither of which a
 * picker inside a dialog can answer, and it would still add chrome to a control the
 * caller had already narrowed to one company by hand. The question a control over a
 * fetched set actually has to ask is much simpler and needs nothing but the set:
 * **can two of these rows arrive looking identical?**
 *
 * Why this exists at all: the company-scoped ERP tables carry per-company value
 * sets that legitimately COLLIDE — account numbers repeat across charts, document
 * numbers are per-company sequences, a warehouse code is per entity. So under the
 * aggregate ("All companies") read two options arrive byte-identical for two
 * different rows and the user picks blind between them. `AccountPicker` shipped
 * that on every tenant-settings GL field, offering "1010 Business Checking Account"
 * twice with nothing to tell them apart (WI e465b198).
 *
 * Two conditions, and both are load-bearing:
 *
 * 1. **The offered set really spans more than one company.** A caller that passed a
 *    company id, a single-company tenant, and a reader with the app bar pinned to
 *    one company all get one company back, where a qualifier is noise on every row
 *    — and width the row's own label needs.
 * 2. **Every one of those companies resolves to a name.** An unresolved company
 *    renders `CompanyIdentity`'s neutral "unassigned" form, so a set that only half
 *    resolves would put the same placeholder on both of two identical rows: it
 *    disambiguates nothing and it looks like an answer. In practice this only holds
 *    while `useMyCompanies()` is in flight, since the rows a reader is served are
 *    bounded by their accessible companies, which is what that query returns.
 *
 * Pass the set BEFORE any search/quick-filter narrowing, or the qualifier flickers
 * away the moment the reader types enough to leave one company standing.
 *
 * The rule this implements: `docs/platform/multi-company-design.md` § 1b.
 */
import { useMemo } from 'react';

import type { CompanyIdentityCompany } from './CompanyIdentity';
import { useCompanyLookup } from './RowCompanyIndicator';

/** The shape any company-owned row satisfies. */
export interface CompanyOwnedRow {
  company_id?: string | null;
}

/**
 * `Map<company_id, company>` when the offered rows need qualifying, else `null`.
 *
 * `null` is the whole gate, so a caller reads
 * `{companyOf && <RowCompanyIndicator company={companyOf.get(row.company_id)} />}`
 * and carries no condition of its own.
 */
export function useCompanyDisambiguation(
  rows: readonly CompanyOwnedRow[],
): Map<string, CompanyIdentityCompany> | null {
  const lookupCompany = useCompanyLookup();

  return useMemo(() => {
    const companyIds = new Set<string>();
    for (const row of rows) if (row.company_id) companyIds.add(row.company_id);
    if (companyIds.size < 2) return null;

    const resolved = new Map<string, CompanyIdentityCompany>();
    for (const companyId of companyIds) {
      const company = lookupCompany(companyId);
      if (!company) return null;
      resolved.set(companyId, company);
    }
    return resolved;
  }, [rows, lookupCompany]);
}
